/**
 * MouseController — Interacción del mouse con el grafo 3D.
 *
 * Responsabilidades:
 *   - Hover sobre nodos (setHoveredNode)
 *   - Click simple → selección + expand + pulse (setSelectedNode)
 *   - Drag con botón izquierdo → mueve el nodo en el plano de la cámara
 *   - Doble click → alterna escala 1x / 2.5x del nodo
 *   - Wheel sobre nodo → ajusta escala individual
 *
 * Mientras hay drag activo, desactiva los OrbitControls para que el usuario
 * pueda arrastrar sin que la cámara rote. Al soltar, los reactiva.
 */

import * as THREE from 'three';
import state from '../core/state';
import { Patient } from '../medical/Patient';
import { SceneManager } from '../visualization/SceneManager';

export class MouseController {
  private raycaster = new THREE.Raycaster();
  private dragPlane = new THREE.Plane();
  private dragOffset = new THREE.Vector3();
  private dragNodeId: string | null = null;
  private patient: Patient | null = null;

  constructor(private sceneManager: SceneManager) {
    this.attach();
  }

  setPatient(patient: Patient | null): void {
    this.patient = patient;
  }

  isDragging(): boolean {
    return this.dragNodeId !== null;
  }

  private attach(): void {
    const canvas = this.sceneManager.renderer.domElement;
    const camera = this.sceneManager.camera;

    const getMouse = (e: MouseEvent): THREE.Vector2 => {
      const rect = canvas.getBoundingClientRect();
      return new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
    };

    const hitTest = (mouse: THREE.Vector2): { nodeId: string; point: THREE.Vector3 } | null => {
      if (!this.patient) return null;
      this.raycaster.setFromCamera(mouse, camera);
      const meshes = Array.from(this.patient.nodes.values()).filter(n => n.visible).map(n => n.mesh);
      const hits = this.raycaster.intersectObjects(meshes);
      if (hits.length > 0) {
        return { nodeId: hits[0].object.userData.nodeId, point: hits[0].point };
      }
      return null;
    };

    canvas.addEventListener('mousemove', (e: MouseEvent) => {
      // Mientras la mano manda, el mouse no debe pelear por hover/select.
      // Drag en curso sí se mantiene (gesture iniciado por mouse manda hasta mouseup).
      if (state.inputOwner() === 'hand' && !this.dragNodeId) return;

      const mouse = getMouse(e);

      // Durante drag, NO actualizamos hover: el mouse pasa sobre otros nodos
      // al arrastrar y los "seleccionaría" sin querer. Solo movemos el nodo.
      if (this.dragNodeId && this.patient) {
        e.preventDefault();
        canvas.style.cursor = 'grabbing';
        this.raycaster.setFromCamera(mouse, camera);
        const target = new THREE.Vector3();
        if (this.raycaster.ray.intersectPlane(this.dragPlane, target)) {
          target.add(this.dragOffset);
          const node = this.patient.getNode(this.dragNodeId);
          if (node) {
            node.position.lerp(target, 0.5);
            node.velocity.set(0, 0, 0);
          }
        }
        return;
      }

      const hit = hitTest(mouse);
      if (hit) {
        state.setHoveredNode(hit.nodeId);
        canvas.style.cursor = 'grab';
      } else {
        state.setHoveredNode(null);
        canvas.style.cursor = 'default';
      }
    });

    canvas.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (state.inputOwner() === 'hand') return;
      const mouse = getMouse(e);
      const hit = hitTest(mouse);
      if (!hit || !this.patient) return;
      const node = this.patient.getNode(hit.nodeId);
      if (!node) return;

      this.dragNodeId = hit.nodeId;
      state.setDraggedNode(hit.nodeId);
      this.sceneManager.controls.enabled = false;
      canvas.style.cursor = 'grabbing';

      const camDir = camera.getWorldDirection(new THREE.Vector3());
      this.dragPlane.setFromNormalAndCoplanarPoint(camDir, node.position);
      this.dragOffset.copy(node.position).sub(hit.point);
    });

    window.addEventListener('mouseup', () => {
      if (!this.dragNodeId) return;
      state.setDraggedNode(null);
      this.dragNodeId = null;
      this.sceneManager.controls.enabled = true;
      canvas.style.cursor = 'default';
    });

    canvas.addEventListener('click', (e: MouseEvent) => {
      if (this.dragNodeId) return; // click durante drag es espurio
      if (state.inputOwner() === 'hand') return;
      const mouse = getMouse(e);
      const hit = hitTest(mouse);
      if (!hit || !this.patient) return;

      state.setSelectedNode(hit.nodeId);
      const node = this.patient.getNode(hit.nodeId);
      if (node) {
        node.expand();
        node.pulse();
      }
    });

    canvas.addEventListener('dblclick', (e: MouseEvent) => {
      if (state.inputOwner() === 'hand') return;
      const mouse = getMouse(e);
      const hit = hitTest(mouse);
      if (!hit || !this.patient) return;
      const node = this.patient.getNode(hit.nodeId);
      if (!node) return;

      if (node.mesh.scale.x > 1.5) {
        node.mesh.scale.setScalar(1);
        node.glow.scale.set(
          node.getBoundingRadius() * 3,
          node.getBoundingRadius() * 3,
          1
        );
      } else {
        node.mesh.scale.setScalar(2.5);
        node.glow.scale.setScalar(node.getBoundingRadius() * 15);
        node.expand();
        state.setSelectedNode(hit.nodeId);
      }
    });

    canvas.addEventListener('wheel', (e: WheelEvent) => {
      if (state.inputOwner() === 'hand') return;
      const mouse = getMouse(e as unknown as MouseEvent);
      const hit = hitTest(mouse);
      if (!hit || !this.patient) return;

      e.preventDefault();
      e.stopPropagation();
      const node = this.patient.getNode(hit.nodeId);
      if (!node) return;
      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      const newScale = Math.max(0.5, Math.min(4.0, node.mesh.scale.x + delta));
      node.mesh.scale.setScalar(newScale);
      node.glow.scale.setScalar(node.getBoundingRadius() * 3 * newScale);
    }, { passive: false });
  }
}

export default MouseController;
